/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { NAV_SUBSCRIPTION_PLANS, RAG_DOCUMENTATION_URL } from "@/constants";
import { IProject } from "@/interfaces/Project.interface";
import { SERVICE_TYPE_SIMPLIFIED } from "@/interfaces/Service.interface";
import replaceUrlParams from "@/utils/string/replaceUrlParams";
import { QuestionCircleFilled, WarningOutlined } from "@ant-design/icons";
import { Alert, Button, Modal } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

export const ManageProjectModal = ({ projectData }: { projectData?: IProject }) => {
  const { workspaceId, projectId } = useParams();
  const params = {
    workspaceId: workspaceId as string,
    projectId: projectId as string,
  };
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const { t } = useTranslation();

  const hasApiKey = projectData
    ? !!projectData.services.find((service) => service.service_type === SERVICE_TYPE_SIMPLIFIED.CACHE)?.api_keys.length
    : false;

  const isFreeTier = projectData ? projectData.subscription_plan === "Free" : false;

  const renderCacheTypeHelp = () => {
    const cacheType = projectData?.services.find((service) => service.service_type === SERVICE_TYPE_SIMPLIFIED.CACHE)?.settings.cache_type;

    if (cacheType === "RAGC") {
      return t("project_single.setup_ragc");
    }
    if (cacheType === "TC") {
      return t("project_single.setup_tc");
    }
  };

  return (
    <>
      <Button className="px-0" type="link" shape="circle" size="large" icon={<QuestionCircleFilled style={{fontSize: '1.35rem'}} />} onClick={() => setHelpModalOpen(true)}></Button>

      <Modal
        centered
        destroyOnClose
        mask
        width={"95%"}
        style={{ maxWidth: 660 }}
        open={helpModalOpen}
        title={t("project_single.setup_title")}
        footer={[]}
        onCancel={() => setHelpModalOpen(false)}
      >
        {!hasApiKey ? (
          <Alert
            className="mt-4"
            icon={<WarningOutlined style={{ alignSelf: "flex-start" }} className="mt-1" />}
            showIcon
            type="warning"
            message={t("project_single.setup_api_key")}
          />
        ) : null}

        <p
          className="mt-4"
          dangerouslySetInnerHTML={{
            __html: t("project_single.setup_integration", {
              docs_url: RAG_DOCUMENTATION_URL,
            }),
          }}
        />

        <p className="mt-4">{renderCacheTypeHelp()}</p>

        <p
          className="mt-4"
          dangerouslySetInnerHTML={{
            __html: t("project_single.setup_instructions", {
              docs_url: RAG_DOCUMENTATION_URL,
            }),
          }}
        />

        {isFreeTier ? (
          <p className="mt-4">
            {t("project_single.setup_subscription")}
            <Link
              to={`${replaceUrlParams(NAV_SUBSCRIPTION_PLANS, {
                ...params,
              })}`}
            >
              {t("common.upgrade").toLowerCase()}
            </Link>
            {"."}
          </p>
        ) : null}
      </Modal>
    </>
  );
};
